import React from 'react';

// Shimmer animation styles
const skeletonStyles = `
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  .skeleton-shimmer {
    background: linear-gradient(
      90deg,
      #e5e7eb 0%,
      #f3f4f6 50%,
      #e5e7eb 100%
    );
    background-size: 1000px 100%;
    animation: shimmer 2s infinite;
  }
`;

// Inject styles once
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = skeletonStyles;
  if (!document.head.querySelector('style[data-skeleton-shimmer]')) {
    styleElement.setAttribute('data-skeleton-shimmer', 'true');
    document.head.appendChild(styleElement);
  }
}

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  className?: string;
  style?: React.CSSProperties;
}

// Base Skeleton component
const BaseSkeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = '20px',
  borderRadius = '8px',
  className = '',
  style = {},
}) => {
  const baseStyle: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    borderRadius: typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius,
    ...style,
  };

  return (
    <div
      className={`skeleton-shimmer ${className}`}
      style={baseStyle}
    />
  );
};

// SkeletonText - Single line of text
export const SkeletonText: React.FC<SkeletonProps> = (props) => (
  <BaseSkeleton height={16} {...props} />
);

// SkeletonCard - Card-shaped skeleton for KPI cards
interface SkeletonCardProps extends SkeletonProps {
  count?: number;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  width = '100%',
  height = '120px',
  count = 1,
  className = '',
  style = {},
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', ...style }}>
      {Array.from({ length: count }).map((_, idx) => (
        <div
          key={idx}
          style={{
            backgroundColor: '#f9fafb',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid #e5e7eb',
          }}
        >
          {/* Header line */}
          <BaseSkeleton
            width="60%"
            height={14}
            borderRadius="6px"
            style={{ marginBottom: '12px' }}
          />

          {/* Main value */}
          <BaseSkeleton
            width="80%"
            height={32}
            borderRadius="8px"
            style={{ marginBottom: '12px' }}
          />

          {/* Footer line */}
          <BaseSkeleton width="50%" height={12} borderRadius="6px" />
        </div>
      ))}
    </div>
  );
};

// SkeletonTable - Table skeleton with header and rows
interface SkeletonTableProps extends Omit<SkeletonProps, 'height'> {
  rows?: number;
  columns?: number;
}

export const SkeletonTable: React.FC<SkeletonTableProps> = ({
  width = '100%',
  rows = 5,
  columns = 4,
  className = '',
  style = {},
}) => {
  const tableWidth = typeof width === 'number' ? `${width}px` : width;
  const columnWidth = `${100 / columns}%`;

  return (
    <div
      style={{
        width: tableWidth,
        backgroundColor: '#f9fafb',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        ...style,
      }}
      className={className}
    >
      {/* Header Row */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
        }}
      >
        {Array.from({ length: columns }).map((_, idx) => (
          <div
            key={`header-${idx}`}
            style={{
              width: columnWidth,
              padding: '12px 16px',
              borderRight: idx < columns - 1 ? '1px solid #e5e7eb' : 'none',
            }}
          >
            <BaseSkeleton width="70%" height={14} borderRadius="6px" />
          </div>
        ))}
      </div>

      {/* Data Rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div
          key={`row-${rowIdx}`}
          style={{
            display: 'flex',
            borderBottom: rowIdx < rows - 1 ? '1px solid #e5e7eb' : 'none',
            backgroundColor: rowIdx % 2 === 0 ? '#ffffff' : '#f9fafb',
          }}
        >
          {Array.from({ length: columns }).map((_, colIdx) => (
            <div
              key={`cell-${rowIdx}-${colIdx}`}
              style={{
                width: columnWidth,
                padding: '12px 16px',
                borderRight: colIdx < columns - 1 ? '1px solid #e5e7eb' : 'none',
              }}
            >
              <BaseSkeleton
                width={`${60 + Math.random() * 30}%`}
                height={14}
                borderRadius="6px"
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

// SkeletonChart - Chart area skeleton
interface SkeletonChartProps extends SkeletonProps {}

export const SkeletonChart: React.FC<SkeletonChartProps> = ({
  width = '100%',
  height = '300px',
  className = '',
  style = {},
}) => {
  const chartHeight = typeof height === 'number' ? `${height}px` : height;
  const chartWidth = typeof width === 'number' ? `${width}px` : width;

  return (
    <div
      style={{
        width: chartWidth,
        backgroundColor: '#f9fafb',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        padding: '16px',
        ...style,
      }}
      className={className}
    >
      {/* Title */}
      <BaseSkeleton
        width="40%"
        height={18}
        borderRadius="6px"
        style={{ marginBottom: '16px' }}
      />

      {/* Chart area */}
      <div
        style={{
          width: '100%',
          height: chartHeight,
          display: 'flex',
          alignItems: 'flex-end',
          gap: '8px',
          justifyContent: 'space-around',
        }}
      >
        {Array.from({ length: 8 }).map((_, idx) => (
          <div key={idx} style={{ flex: 1 }}>
            <BaseSkeleton
              width="100%"
              height={`${60 + Math.random() * 100}px`}
              borderRadius="6px"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// SkeletonList - List of items skeleton
interface SkeletonListProps extends SkeletonProps {
  count?: number;
}

export const SkeletonList: React.FC<SkeletonListProps> = ({
  width = '100%',
  count = 6,
  className = '',
  style = {},
}) => {
  const listWidth = typeof width === 'number' ? `${width}px` : width;

  return (
    <div
      style={{
        width: listWidth,
        ...style,
      }}
      className={className}
    >
      {Array.from({ length: count }).map((_, idx) => (
        <div
          key={idx}
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '16px',
            alignItems: 'flex-start',
          }}
        >
          {/* Avatar/Icon */}
          <BaseSkeleton
            width={40}
            height={40}
            borderRadius="8px"
            style={{ flexShrink: 0 }}
          />

          {/* Content */}
          <div style={{ flex: 1 }}>
            <BaseSkeleton
              width="60%"
              height={16}
              borderRadius="6px"
              style={{ marginBottom: '8px' }}
            />
            <BaseSkeleton
              width="90%"
              height={14}
              borderRadius="6px"
              style={{ marginBottom: '8px' }}
            />
            <BaseSkeleton width="70%" height={12} borderRadius="6px" />
          </div>

          {/* Action area */}
          <BaseSkeleton
            width={80}
            height={32}
            borderRadius="6px"
            style={{ flexShrink: 0 }}
          />
        </div>
      ))}
    </div>
  );
};

// SkeletonDashboard - Full dashboard page skeleton
interface SkeletonDashboardProps {
  className?: string;
  style?: React.CSSProperties;
}

export const SkeletonDashboard: React.FC<SkeletonDashboardProps> = ({
  className = '',
  style = {},
}) => {
  return (
    <div
      style={{
        width: '100%',
        padding: '24px',
        ...style,
      }}
      className={className}
    >
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <BaseSkeleton
          width="30%"
          height={32}
          borderRadius="8px"
          style={{ marginBottom: '12px' }}
        />
        <BaseSkeleton width="50%" height={16} borderRadius="6px" />
      </div>

      {/* KPI Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}
      >
        {Array.from({ length: 4 }).map((_, idx) => (
          <SkeletonCard key={idx} />
        ))}
      </div>

      {/* Charts Section */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}
      >
        <SkeletonChart height="250px" />
        <SkeletonChart height="250px" />
      </div>

      {/* Table Section */}
      <div style={{ marginBottom: '16px' }}>
        <BaseSkeleton
          width="25%"
          height={24}
          borderRadius="8px"
          style={{ marginBottom: '16px' }}
        />
        <SkeletonTable rows={5} columns={4} />
      </div>
    </div>
  );
};

export default BaseSkeleton;
